import { useEffect, useId, useRef, useState } from 'react';
import { MenuCaret } from './MenuCaret';

/**
 * Content → Articles: "Source: All ▼" (Feed Articles design D) — All, Made in
 * TinyCMS, and each feed the articles came from, with counts. "Not brought in
 * yet" is the banner's "Show them": feed articles not in TinyCMS. Only shown
 * when some article came from a feed.
 */
export interface SourceCount {
  id: string | null;
  name?: string | null;
  type?: string | null;
  count: number;
  waiting: number;
}

/** 'all' | 'app' | 'waiting' | a data source id — topicsPage's source. */
export type SourceValue = string;

export function sourceLabel(value: SourceValue, sources: SourceCount[]): string {
  if (value === 'all') return 'All';
  if (value === 'app') return 'Made in tinycms';
  if (value === 'waiting') return 'Not brought in yet';
  return sources.find((s) => s.id === value)?.name || 'A feed';
}

export function SourceFilter({
  sources,
  value,
  onChange,
}: {
  sources: SourceCount[];
  value: SourceValue;
  onChange: (value: SourceValue) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const all = sources.reduce((n, s) => n + s.count, 0);
  const app = sources.find((s) => s.id === null)?.count ?? 0;
  const feeds = sources.filter((s) => s.id !== null);
  const waiting = feeds.reduce((n, s) => n + s.waiting, 0);
  const options: Array<{ value: SourceValue; label: string; count: number }> = [
    { value: 'all', label: 'All sources', count: all },
    { value: 'app', label: 'Made in tinycms', count: app },
    ...feeds.map((s) => ({ value: s.id as string, label: s.name || 'A feed', count: s.count })),
    ...(waiting ? [{ value: 'waiting', label: 'Not brought in yet', count: waiting }] : []),
  ];
  const pick = (v: SourceValue) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <span className="ink-topic-filter" ref={wrap}>
      <button
        type="button"
        className="ink-chip"
        data-active={value !== 'all' ? 'true' : 'false'}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
      >
        Source: {sourceLabel(value, sources)} <MenuCaret />
      </button>
      {open ? (
        <div className="ink-topic-panel">
          <div role="listbox" id={listId} aria-label="Sources">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                data-active={o.value === value ? 'true' : 'false'}
                onClick={() => pick(o.value)}
              >
                <span>{o.label}</span>
                <span>{o.count.toLocaleString()}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </span>
  );
}
